<template>
	<div :class="['sidebar-chat-list', { active, 'force-compressed': forceCompressed }]">
		<div class="container-buttons">
			<AppButton class="button-chat" icon-left="new-chat" outline :text="getString('new_chat')" @click="clickNewChat" />
			<AppTextInput iconName="icon-magnify" ref="search-input-modal" @changing-text="v=>searchText=v" :placeholder="getString('search')" />
		</div>
		<ChatList @clicked-chat="toggleSidebar(false)" :period-divided-chats="periodDividedChats" ref="chat-list" />
		<div class="container-buttons">
			<AppButton class="button-chat" icon-left="Icon-ticket-outline" outline :text="getString('open_ticket')" @click="redirectToTicket"/>
			<AppButton class="button-chat" icon-left="Icon-wiky" outline :text="getString('show_wiki')" @click="redirectToHelpDesk"/>
		</div>
		<div @click="toggleSidebar()" class="button-close">
			<img class="icon-close" :alt="getString('open_ticket')" :src="'https://img.qromo.io/img/icons/icon-list-menu'+(userStore.darkMode ? '-dark' : '')+'.svg'"/>
		</div>
	</div>
</template>

<script>
import AppButton from "@/shared/components/AppButton.vue";
import {useLanguageStore} from "@/shared/stores";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ChatList from "@/console-new/components/help/chat/ChatList.vue";
import {useUserStore, useOpenAIChatsStore} from "@/console-new/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let openAIChatsStore = useOpenAIChatsStore();
		return { getString: languageStore.getString, userStore, openAIChatsStore };
	},
	name: "ChatSidebar",
	components: { AppTextInput, AppButton, ChatList },
	props: {
		forceCompressed: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			active: false,
			searchText: null,
		}
	},
	computed: {
		periodDividedChats() {
			let chats = this.openAIChatsStore?.chats || {};
			let chatsArray = Object.values(chats);
			let todayChats = [];
			let yesterdayChats = [];
			let last7DaysChats = [];
			let previousChats = [];
			let today = new Date();
			today.setHours(0, 0, 0, 0);
			let yesterday = new Date(today);
			yesterday.setDate(today.getDate() - 1);
			let last7Days = new Date(today);
			last7Days.setDate(today.getDate() - 7);
			let searchWord = this.searchText ? this.searchText.trim().toLowerCase().replaceAll(" ", "") : null;
			chatsArray.forEach(chat => {
				if(!searchWord
					|| this.openAIChatsStore.activeChatId === chat.ai_chat_id
					|| (chat.name || '').trim().toLowerCase().replaceAll(" ", "").includes(searchWord)
				) {
					let chatDate = new Date(chat.updated_at);
					if (chat.ai_chat_id === 'new') {
						todayChats.unshift(chat);
					} else if (chatDate >= today) {
						todayChats.push(chat);
					} else if (chatDate >= yesterday) {
						yesterdayChats.push(chat);
					} else if (chatDate >= last7Days) {
						last7DaysChats.push(chat);
					} else {
						previousChats.push(chat);
					}
				}
			});
			return {
				today: {
					title: this.getString('today'),
					chats: todayChats.sort((b, a) => a.updated_at - b.updated_at),
				},
				yesterday: {
					title: this.getString('yesterday'),
					chats: yesterdayChats.sort((b, a) => a.updated_at - b.updated_at),
				},
				last7Days: {
					title: this.getString('last_7_days'),
					chats: last7DaysChats.sort((b, a) => a.updated_at - b.updated_at),
				},
				previous: {
					title: this.getString('previous'),
					chats: previousChats.sort((b, a) => a.updated_at - b.updated_at),
				},
			};
		}
	},
	methods: {
		clickNewChat() {
			if(this.openAIChatsStore.loadingChats) {
				return
			}
			this.openAIChatsStore.newChat();
			this.toggleSidebar(false);
			if(this.$refs['chat-list']?.$el) {
				this.$refs['chat-list'].$el.scrollTop = 0;
			}
		},
		toggleSidebar(newState = !this.active) {
			this.active = newState;
		},
		redirectToHelpDesk() {
			if(this.openAIChatsStore.loadingChats) {
				return
			}
			window.open("https://qromo-help.freshdesk.com/support/home", "_blank");
		},
		redirectToTicket() {
			if(this.openAIChatsStore.loadingChats) {
				return
			}
			window.open("https://qromo-help.freshdesk.com/support/tickets/new", "_blank");
		},
	}
};
</script>

<style scoped lang="scss">
.sidebar-chat-list {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 20px;
	height: 100%;
	max-width: 320px;
	width: 320px;
	border-right: 1px solid var(--color-alpha-4);
	background-color: var(--color-bg-primary-1);
	@media screen and (max-width: 1440px) {
		max-width: 270px;
		width: 270px;
	}
	@media screen and (max-width: 1260px) {
		max-width: 320px;
		width: 320px;
		position: absolute;
		z-index: 2;
		transform: translateX(-320px);
		transition: transform 0.3s $transition;
		pointer-events: none;
		&.active {
			transform: translateX(0);
			pointer-events: auto;
			box-shadow: 5px 0 35px var(--color-alpha-1);
		}
	}
	@media screen and (max-width: 450px) {
		max-width: 270px;
		width: 270px;
		transform: translateX(-270px);
	}
	@media screen and (max-width: 360px) {
		max-width: 220px;
		width: 220px;
		transform: translateX(-220px);
	}
	&.force-compressed {
		max-width: 270px;
		width: 270px;
		position: absolute;
		z-index: 2;
		transform: translateX(-270px);
		transition: transform 0.3s $transition;
		pointer-events: none;
		&.active {
			transform: translateX(0);
			pointer-events: auto;
			box-shadow: 5px 0 35px var(--color-alpha-1);
		}
		.button-close {
			display: flex;
		}
	}
	.container-buttons {
		display: flex;
		flex-direction: column;
		gap: 15px;
		height: 97px;
		.base-button.outline.button-chat {
			justify-content: flex-start;
			width: 100%;
			min-height: 42px;
			border: none;
			background-color: var(--color-alpha-1);
			box-shadow: none;
			transition: all 0.3s $transition;
			&:hover {
				background-color: var(--color-alpha-2);
			}
		}
	}
	:deep(.input-wrapper) {
		width: 100%;
	}
	.button-close {
		display: none;
		position: absolute;
		top: 8px;
		right: -50px;
		justify-content: center;
		align-items: center;
		width: 40px;
		height: 40px;
		background-color: var(--color-bg-primary-1);
		border-radius: 13px;
		cursor: pointer;
		box-shadow: none;
		transition: all 0.3s $transition;
		pointer-events: auto;
		z-index: 2;
		@media screen and (max-width: 1260px) {
			display: flex;
		}
		.icon-close {
			width: 20px;
			height: 20px;
		}
	}
}
</style>